import { ref, reactive } from 'vue';
import ApiService from '@/core/services/api.service';
import { formatDate } from '@/utils/common';

export default function useCustomerTransactions() {
  const transactions = ref([]);
  const totalItems = ref(0);
  const totalPages = ref(0);
  const currentPage = ref(1);
  const isLoading = ref(false);

  const filters = reactive({
    fromDate: '',
    toDate: '',
    type: null,
    source: null,
    status: null,
    search: '',
  });

  const transactionTypes = [
    { value: null, text: 'Tất cả loại giao dịch' },
    { value: 'EARN', text: 'Tích điểm' },
    { value: 'REDEEM', text: 'Đổi điểm' },
    { value: 'ADJUST', text: 'Điều chỉnh' },
    { value: 'EXPIRE', text: 'Hết hạn' },
    { value: 'CANCEL', text: 'Hủy' },
    { value: 'TRANSFER', text: 'Chuyển điểm' },
  ];

  const sourcesOptions = [
    { value: null, text: 'Tất cả nguồn gốc' },
    { value: 'ORDER', text: 'Đơn hàng' },
    { value: 'MANUAL', text: 'Thủ công' },
    { value: 'PROMOTION', text: 'Khuyến mãi' },
    { value: 'SYSTEM', text: 'Hệ thống' },
    { value: 'REFERRAL', text: 'Giới thiệu' },
    { value: 'EVENT', text: 'Sự kiện' },
  ];

  const statusOptions = [
    { value: null, text: 'Tất cả trạng thái' },
    { value: 'PENDING', text: 'Đang xử lý' },
    { value: 'COMPLETED', text: 'Hoàn thành' },
    { value: 'CANCELLED', text: 'Đã hủy' },
    { value: 'DISPUTED', text: 'Tranh chấp' },
  ];

  const fetchTransactions = async (customerId, page = 1) => {
    if (!customerId) return;

    isLoading.value = true;
    currentPage.value = page;

    try {
      const params = {
        page: page,
        limit: 10,
        search: filters.search,
      };

      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.type) params.types = filters.type;
      if (filters.source) params.sources = filters.source;
      if (filters.status) params.status = filters.status;

      const response = await ApiService.query(
        `/v1/customer-points/${customerId}/transactions`,
        { params },
      );

      if (response.data && response.data.data) {
        transactions.value = response.data.data.data;

        if (response.data.data.paging) {
          totalItems.value = parseInt(response.data.data.paging.total) || 0;

          const limit = parseInt(response.data.data.paging.limit) || 10;
          totalPages.value = Math.ceil(totalItems.value / limit);
        } else {
          totalItems.value = response.data.data.total || 0;
          totalPages.value = Math.ceil(totalItems.value / 10);
        }
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const applyFilters = (customerId) => {
    fetchTransactions(customerId, 1);
  };

  const resetFilters = (customerId) => {
    filters.fromDate = '';
    filters.toDate = '';
    filters.type = null;
    filters.source = null;
    filters.status = null;
    filters.search = '';

    fetchTransactions(customerId, 1);
  };

  const formatTransactionType = (type) => {
    const typeMap = {
      EARN: 'Tích điểm',
      REDEEM: 'Đổi điểm',
      ADJUST: 'Điều chỉnh',
      EXPIRE: 'Hết hạn',
      CANCEL: 'Hủy',
      TRANSFER: 'Chuyển điểm',
    };

    return typeMap[type] || type;
  };

  const formatTransactionSource = (source) => {
    const sourceMap = {
      ORDER: 'Đơn hàng',
      MANUAL: 'Thủ công',
      PROMOTION: 'Khuyến mãi',
      SYSTEM: 'Hệ thống',
      REFERRAL: 'Giới thiệu',
      EVENT: 'Sự kiện',
    };

    return sourceMap[source] || source;
  };

  const formatTransactionStatus = (status) => {
    const statusMap = {
      PENDING: 'Đang xử lý',
      COMPLETED: 'Hoàn thành',
      CANCELLED: 'Đã hủy',
      DISPUTED: 'Tranh chấp',
    };

    return statusMap[status] || status;
  };

  const getStatusVariant = (status) => {
    const variantMap = {
      PENDING: 'warning',
      COMPLETED: 'success',
      CANCELLED: 'danger',
      DISPUTED: 'info',
    };

    return variantMap[status] || 'secondary';
  };

  const getTypeVariant = (type) => {
    const variantMap = {
      EARN: 'success',
      REDEEM: 'primary',
      ADJUST: 'info',
      EXPIRE: 'warning',
      CANCEL: 'danger',
      TRANSFER: 'secondary',
    };

    return variantMap[type] || 'secondary';
  };

  return {
    transactions,
    totalItems,
    totalPages,
    currentPage,
    isLoading,
    filters,
    transactionTypes,
    sourcesOptions,
    statusOptions,
    fetchTransactions,
    applyFilters,
    resetFilters,
    formatTransactionType,
    formatTransactionSource,
    formatTransactionStatus,
    getStatusVariant,
    getTypeVariant,
    formatDate,
  };
}
